import {
  validateMapDocument,
  MAP_DOCUMENT_MAX_BYTES,
} from "../shared/validation";
import type {
  MapPin,
  MapRoute,
  MapRouteStyle,
  PublishedMap,
} from "../shared/types";

export const MAP_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,79}$/;
export const MAX_PUBLISHED_BYTES = MAP_DOCUMENT_MAX_BYTES + 1024;

export class EmbedError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "EmbedError";
  }
}

export function mapURL(mapId: string, apiBase: string): URL {
  if (typeof mapId !== "string" || !MAP_ID_PATTERN.test(mapId))
    throw new EmbedError("invalid_map_id", "A valid map ID is required.");
  let base: URL;
  try {
    base = new URL(apiBase);
  } catch {
    throw new EmbedError(
      "invalid_service_url",
      "The map service URL must be an absolute HTTP or HTTPS URL.",
    );
  }
  if (
    !["http:", "https:"].includes(base.protocol) ||
    base.username ||
    base.password ||
    base.search ||
    base.hash
  ) {
    throw new EmbedError(
      "invalid_service_url",
      "The map service URL must be an HTTP or HTTPS URL without credentials, query parameters or a fragment.",
    );
  }
  return new URL(
    `${base.pathname.replace(/\/$/, "")}/api/v1/maps/${mapId}`,
    base.origin,
  );
}

export function validatePins(value: unknown): MapPin[] {
  if (!Array.isArray(value) || value.length > 500)
    throw new EmbedError(
      "invalid_pins",
      "Pins must be an array containing at most 500 items.",
    );
  const ids = new Set<string>();
  return value.map((pin: unknown) => {
    if (!pin || typeof pin !== "object")
      throw new EmbedError(
        "invalid_pins",
        "Every pin needs an ID and a map location ID.",
      );
    const p = pin as Record<string, unknown>;
    if (
      typeof p.id !== "string" ||
      !p.id ||
      p.id.length > 120 ||
      ids.has(p.id) ||
      typeof p.locationId !== "string" ||
      !p.locationId ||
      p.locationId.length > 120 ||
      (p.label !== undefined &&
        (typeof p.label !== "string" || p.label.length > 200)) ||
      (p.number !== undefined &&
        (!Number.isInteger(p.number) ||
          (p.number as number) < 0 ||
          (p.number as number) > 9999)) ||
      (p.completed !== undefined && typeof p.completed !== "boolean")
    ) {
      throw new EmbedError(
        "invalid_pins",
        "Pins need unique IDs, valid map location IDs and optional labels, numbers and completion flags.",
      );
    }
    ids.add(p.id);
    return {
      id: p.id,
      locationId: p.locationId,
      ...(p.label !== undefined ? { label: p.label as string } : {}),
      ...(p.number !== undefined ? { number: p.number as number } : {}),
      ...(p.completed !== undefined
        ? { completed: p.completed as boolean }
        : {}),
    };
  });
}

export function validateRoute(value: unknown): MapRoute | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "object")
    throw new EmbedError(
      "invalid_route",
      "A route needs a from and a to location ID.",
    );
  const route = value as Record<string, unknown>;
  if (
    typeof route.from !== "string" ||
    !route.from ||
    route.from.length > 120 ||
    typeof route.to !== "string" ||
    !route.to ||
    route.to.length > 120
  ) {
    throw new EmbedError(
      "invalid_route",
      "A route needs a from and a to location ID.",
    );
  }
  return { from: route.from, to: route.to };
}

/** Numeric controls reject invalid values instead of silently changing an integration's intent. */
export function validateControlNumber(
  value: unknown,
  name: string,
  min = -Infinity,
  max = Infinity,
): number {
  if (
    typeof value !== "number" ||
    !Number.isFinite(value) ||
    value < min ||
    value > max
  )
    throw new EmbedError(
      "invalid_control",
      `${name} must be a finite number${Number.isFinite(min) && Number.isFinite(max) ? ` from ${min} to ${max}` : ""}.`,
    );
  return value;
}

export function validateRouteStyle(value: unknown): MapRouteStyle {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new EmbedError(
      "invalid_route_style",
      "Route style must be an object.",
    );
  const style = value as Record<string, unknown>;
  if (
    Object.keys(style).some(
      (key) => !["contextSaturation", "endpointLift"].includes(key),
    ) ||
    (style.contextSaturation !== undefined &&
      (typeof style.contextSaturation !== "number" ||
        !Number.isFinite(style.contextSaturation) ||
        style.contextSaturation < 0 ||
        style.contextSaturation > 1)) ||
    (style.endpointLift !== undefined &&
      (typeof style.endpointLift !== "number" ||
        !Number.isFinite(style.endpointLift) ||
        style.endpointLift < 0 ||
        style.endpointLift > 100))
  )
    throw new EmbedError(
      "invalid_route_style",
      "Route style accepts contextSaturation from 0 to 1 and endpointLift from 0 to 100.",
    );
  return {
    contextSaturation: (style.contextSaturation as number | undefined) ?? 1,
    endpointLift: (style.endpointLift as number | undefined) ?? 0,
  };
}

export function validatePublishedMap(
  input: unknown,
  expectedMapId: string,
): PublishedMap {
  try {
    if (!input || typeof input !== "object")
      throw new Error("Missing document");
    const value = input as Record<string, unknown>;
    if (
      !Number.isSafeInteger(value.revision) ||
      (value.revision as number) < 1 ||
      typeof value.publishedAt !== "string" ||
      !Number.isFinite(Date.parse(value.publishedAt))
    )
      throw new Error("Invalid publication");
    const map = validateMapDocument(value.map);
    if (map.id !== expectedMapId) throw new Error("Map ID mismatch");
    return {
      map,
      revision: value.revision as number,
      publishedAt: value.publishedAt,
    };
  } catch {
    throw new EmbedError(
      "invalid_response",
      "The map service returned an invalid map.",
    );
  }
}

export function validateLocations(
  publication: PublishedMap,
  pins: MapPin[],
  route: MapRoute | null,
): void {
  const locations = new Set(
    publication.map.locations.map((location) => location.id),
  );
  if (pins.some((pin) => !locations.has(pin.locationId)))
    throw new EmbedError(
      "unknown_location",
      "A pin refers to a location that is not on this map.",
    );
  if (route && (!locations.has(route.from) || !locations.has(route.to)))
    throw new EmbedError(
      "unknown_location",
      "The route refers to a location that is not on this map.",
    );
}

export async function readPublishedMap(
  response: Response,
  mapId: string,
): Promise<PublishedMap> {
  if (!response.ok) {
    throw response.status === 404
      ? new EmbedError("map_not_found", "This map is not currently available.")
      : new EmbedError(
          "service_unavailable",
          "The map service is temporarily unavailable.",
        );
  }
  if (
    !response.headers
      .get("content-type")
      ?.toLowerCase()
      .includes("application/json")
  )
    throw new EmbedError(
      "invalid_response",
      "The map service returned an invalid response.",
    );
  const declaredBytes = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredBytes) && declaredBytes > MAX_PUBLISHED_BYTES)
    throw new EmbedError("invalid_response", "The map is too large to load.");
  if (!response.body)
    throw new EmbedError(
      "invalid_response",
      "The map service returned an empty response.",
    );
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let length = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      length += value.byteLength;
      if (length > MAX_PUBLISHED_BYTES) {
        await reader.cancel();
        throw new EmbedError(
          "invalid_response",
          "The map is too large to load.",
        );
      }
      chunks.push(value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  let input: unknown;
  try {
    input = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch {
    throw new EmbedError(
      "invalid_response",
      "The map service returned an invalid response.",
    );
  }
  return validatePublishedMap(input, mapId);
}
